/* Sport Tech Store - colour swatches.
   Product page: the colour's own photo is the swatch, so the photo the theme
   rendered is left alone and only colours without a photo get a flat chip.
   Product grid: a row of tiny photos under a card reads as noise, so there the
   dominant colour of each colour's own photo is measured and painted as a dot.
   No colour dictionary and no metafields: a colour that arrives tomorrow gets
   the right dot as soon as it has a photo.
   Anything that fails falls back silently to what the theme rendered. */
(function () {
  'use strict';

  var SEL = '.col-swatch li.color > a > span, .swatches__swatch--color > label.swatches__form--label';
  var MARK = 'data-stswatch';
  var NEUTRAL = '#e9e9e9';
  var CACHE_KEY = 'sts-swatch-colors-v2';
  var MAX_CACHE = 2000;
  var PARALLEL = 6;
  var SAMPLE = 64;
  var BG_TOLERANCE = 30;   /* how close to its neighbour a pixel must be to count as more backdrop */
  var ALPHA_MIN = 200;

  var cache = {};
  try { cache = JSON.parse(localStorage.getItem(CACHE_KEY) || '{}') || {}; } catch (e) { cache = {}; }

  var dirty = false, saveTimer = null;
  function save() {
    if (!dirty) return;
    dirty = false;
    try {
      var keys = Object.keys(cache);
      if (keys.length > MAX_CACHE) {
        var trimmed = {};
        keys.slice(keys.length - MAX_CACHE).forEach(function (k) { trimmed[k] = cache[k]; });
        cache = trimmed;
      }
      localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (e) { /* private mode or quota - the page still works */ }
  }
  function scheduleSave() { dirty = true; clearTimeout(saveTimer); saveTimer = setTimeout(save, 400); }

  function hx(n) { n = Math.max(0, Math.min(255, Math.round(n))); return (n < 16 ? '0' : '') + n.toString(16); }
  function rgb(r, g, b) { return '#' + hx(r) + hx(g) + hx(b); }

  function urlOf(span) {
    /* The inline style is the normal source. Some contexts (the theme editor,
       a section re-render) hand the photo over in a stylesheet instead, so
       fall back to what the browser actually computed - otherwise a colour
       that HAS a photo would be mistaken for one that has none and painted
       over with a flat chip. */
    var raw = span.style.backgroundImage || '';
    if (!raw || raw === 'none') {
      raw = (window.getComputedStyle ? window.getComputedStyle(span).backgroundImage : '') || '';
    }
    var m = /url\((['"]?)(.*?)\1\)/.exec(raw);
    var u = m && m[2];
    if (!u || u === 'none') return null;
    if (u.indexOf('//') === 0) u = window.location.protocol + u;
    /* An empty url() resolves to the page itself - that is a colour with no
       photo of its own, not an image. Anything without a picture extension is
       treated the same way. */
    if (u.split('?')[0] === window.location.href.split('?')[0]) return null;
    if (!/\.(png|jpe?g|webp|gif|avif)(\?|$)/i.test(u)) return null;
    return u;
  }
  function small(u) { return u.replace(/_(\d+)x\./, '_64x.'); }

  /* A product-page swatch keeps its photo; a grid dot does not. */
  function isProductPage(el) {
    return !!(el.className && String(el.className).indexOf('swatches__form--label') !== -1);
  }

  function paint(span, hex) {
    span.style.backgroundImage = 'none';
    span.style.backgroundColor = hex;
  }

  /* For a colour with no photo: let the browser say whether the name is one it
     already knows. "Light Blue" -> lightblue, "TNF Black" -> black,
     "Obsidian/Varsity Maize" -> nothing, so a neutral dot. Nothing to maintain
     here: it is the browser's own list, and an unknown name stays neutral. */
  var probe = document.createElement('span');
  function known(v) {
    if (!v) return null;
    probe.style.color = '';
    probe.style.color = v;
    return probe.style.color ? v : null;
  }
  function nameColor(name) {
    if (!name) return null;
    var joined = name.toLowerCase().replace(/[^a-z]/g, '');
    if (known(joined)) return joined;
    var parts = name.toLowerCase().split(/[^a-z]+/);
    for (var i = parts.length - 1; i >= 0; i--) {
      if (known(parts[i])) return parts[i];
    }
    return null;
  }

  /* Dominant colour of the product itself.
     The backdrop is flood-filled inwards from the border, so a white jacket on
     a white studio background survives while the background does not. What is
     left is bucketed into a coarse histogram and the fullest bucket wins -
     a two-tone sneaker reports its larger area rather than a muddy average. */
  function dominant(url, done) {
    var im = new Image();
    im.crossOrigin = 'anonymous';
    im.onerror = function () { done(null); };
    im.onload = function () {
      try {
        var W = SAMPLE;
        var H = Math.max(1, Math.round(im.naturalHeight * W / im.naturalWidth));
        var c = document.createElement('canvas');
        c.width = W; c.height = H;
        var x = c.getContext('2d', { willReadFrequently: true }) || c.getContext('2d');
        x.drawImage(im, 0, 0, W, H);
        var d = x.getImageData(0, 0, W, H).data;
        var N = W * H;
        var out = new Uint8Array(N);       /* 1 = backdrop or transparent */
        var i, j, px, py, nx, ny;

        for (i = 0; i < N; i++) if (d[i * 4 + 3] < ALPHA_MIN) out[i] = 1;

        var q = [];
        for (px = 0; px < W; px++) { q.push(px); q.push((H - 1) * W + px); }
        for (py = 0; py < H; py++) { q.push(py * W); q.push(py * W + W - 1); }

        var D = [[1, 0], [-1, 0], [0, 1], [0, -1]];
        while (q.length) {
          i = q.pop();
          if (out[i] === 1) continue;
          out[i] = 1;
          px = i % W; py = (i / W) | 0;
          for (var k = 0; k < 4; k++) {
            nx = px + D[k][0]; ny = py + D[k][1];
            if (nx < 0 || ny < 0 || nx >= W || ny >= H) continue;
            j = ny * W + nx;
            if (out[j]) continue;
            if (d[j * 4 + 3] < ALPHA_MIN) { out[j] = 1; continue; }
            if (Math.abs(d[i * 4] - d[j * 4]) + Math.abs(d[i * 4 + 1] - d[j * 4 + 1]) +
                Math.abs(d[i * 4 + 2] - d[j * 4 + 2]) < BG_TOLERANCE) q.push(j);
          }
        }

        var bins = {}, best = null, kept = 0;
        for (i = 0; i < N; i++) {
          if (out[i]) continue;
          var r = d[i * 4], g = d[i * 4 + 1], b = d[i * 4 + 2];
          kept++;
          var key = (r >> 4) * 256 + (g >> 4) * 16 + (b >> 4);
          var o = bins[key] || (bins[key] = { n: 0, r: 0, g: 0, b: 0 });
          o.n++; o.r += r; o.g += g; o.b += b;
          if (!best || o.n > best.n) best = o;
        }
        /* Almost nothing left means the photo is not a product shot we can read. */
        if (!best || kept < N * 0.02) return done(null);
        done(rgb(best.r / best.n, best.g / best.n, best.b / best.n));
      } catch (e) { done(null); }
    };
    im.src = url;
  }

  var queue = [], running = 0;
  function pump() {
    while (running < PARALLEL && queue.length) {
      (function (job) {
        running++;
        dominant(job.url, function (hex) {
          running--;
          if (hex) { cache[job.key] = hex; scheduleSave(); paint(job.span, hex); }
          else { var n = nameColor(job.title); if (n) paint(job.span, n); }
          pump();
        });
      })(queue.shift());
    }
  }

  function titleOf(el) {
    /* Grid: the value sits on the <li>. Product page: on the radio input. */
    var li = el.parentNode && el.parentNode.parentNode;
    var t = li && li.getAttribute ? li.getAttribute('data-option-title') : null;
    if (t) return t;
    var input = el.parentNode && el.parentNode.querySelector ? el.parentNode.querySelector('input') : null;
    return input ? input.value : null;
  }

  function scan() {
    var list = document.querySelectorAll(SEL);
    for (var i = 0; i < list.length; i++) {
      var span = list[i];
      if (span.getAttribute(MARK)) continue;
      var u = urlOf(span);
      if (!u) {
        /* No photo of its own: a flat chip from the colour's name. */
        span.setAttribute(MARK, 'chip');
        span.style.backgroundImage = 'none';
        span.style.backgroundColor = nameColor(titleOf(span)) || NEUTRAL;
        continue;
      }
      if (isProductPage(span)) {
        /* The photo stays - that is the thumbnail. The colour name the theme
           put behind it is dropped: on a cut-out PNG it tinted the garment. */
        span.setAttribute(MARK, 'photo');
        span.style.backgroundColor = '#ffffff';
        continue;
      }
      span.setAttribute(MARK, '1');
      var key = u.replace(/^https?:/, '');
      if (cache[key]) { paint(span, cache[key]); continue; }
      queue.push({ span: span, url: small(u), key: key, title: titleOf(span) });
    }
    pump();
  }

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  ready(function () {
    scan();
    if (!window.MutationObserver) return;
    var t = null;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(scan, 200);
    }).observe(document.body, { childList: true, subtree: true });
  });
})();